<template>
  <div class="ui-house-suggest">
    <ui-input
      v-model="query"
      ref="query"
      :label="label"
      autocomplete="off"
      :placeholder="placeholder"
      :required="required"
      validate="house"
      clearable
      class="ui-house-suggest__input"
      @input="getSuggestions"
      @focus="onFocus"
      @blur="onBlur"
    />

    <div class="ui-house-suggest__suggestions">
      <ui-dropdown
        :visible="showSuggestions"
      >
        <div
          v-for="(suggestion, ind) of formattedSuggestions"
          :key="ind"
          class="ui-house-suggest__suggestion"
          @click="select(suggestion)"
        >
          {{ suggestion.text }}
          <f-sprite-icon
            v-if="checkSelected(suggestion)"
            :icon-link="$options.icons.IconCheck.id"
          />
        </div>
      </ui-dropdown>
    </div>
  </div>
</template>

<script>
import { debounce } from 'throttle-debounce';

import IconCheck from '@icons/check.svg';

const addressToString = ({ number, block, building } = {}) => {
  const address = [];

  number && address.push(`д. ${number}`);
  block && address.push(`корп. ${block}`);
  building && address.push(`стр. ${building}`);

  return address.join(' ');
};

export default {
  name: 'ui-house-suggest',

  icons: {
    IconCheck,
  },

  props: {
    value: {
      type: Object,
      default: () => ({}),
    },

    required: {
      type: Boolean,
      default: false,
    },

    streetId: {
      type: String,
      default: '',
    },

    placeholder: {
      type: String,
      default: '',
    },

    label: {
      type: String,
      default: 'Дом *',
    },
  },

  data: () => ({
    isMounted: false,
    query: '',
    suggestions: [],
    isSelectedFromSuggest: false,
    isFocused: false,
  }),

  computed: {
    formattedSuggestions() {
      return this.suggestions.map(s => ({
        ...s,
        text: addressToString(s),
      }));
    },
    showSuggestions() {
      return this.isMounted && this.isFocused && !!this.suggestions.length;
    },
  },

  watch: {
    value: {
      immediate: true,
      handler(val) {
        this.query = val?.number && (val?.building || val?.block)
          ? addressToString(val)
          : val?.number;
      },
    },
  },

  mounted() {
    this.isMounted = true;
  },

  methods: {
    select(house) {
      if (house) {
        this.isSelectedFromSuggest = true;
        this.$emit('input', house);
      }
    },

    getSuggestions: debounce(300, false, function (query = this.query) {
      if (query.length < 1 || !this.streetId) {
        if (this.suggestions.length) this.suggestions = [];
        return;
      }
      this.$store.$api.findHouses({ query, streetId: this.streetId }).then(houses => {
        this.suggestions = houses;
      }).catch(() => {
        console.warn(`По запросу "${this.query}" ничего не найдено`);
      });
    }),

    // для внешнего вызова
    checkValidOutside() {
      if (this.$refs.query) {
        return this.$refs.query.checkValidOutside();
      }
    },

    onFocus() {
      this.isFocused = true;
      this.getSuggestions();
    },

    onBlur() {
      if (!this.isSelectedFromSuggest && this.query !== addressToString(this.value)) {
        this.$emit('input', { number: this.query });
      }
      this.isFocused = false;
      this.isSelectedFromSuggest = false;
      this.suggestions = [];
    },

    checkSelected(suggestion) {
      return Object.keys(this.value).every(key => suggestion[key] === this.value[key]);
    }
  },
};
</script>

<style lang="scss">
.ui-house-suggest {
  position: relative;

  &__input {
    &.ui-input p.error {
      line-height: 1;
    }
  }

  &__suggestions {
    position: absolute;
    top: 64px;
    left: 0;
    right: 0;

    .ui-dropdown {
      margin-top: 8px;
      min-width: 100%;
      width: auto;
    }

    .ui-dropdown-scrollview {
      max-height: 216px;
    }
  }

  &__suggestion {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 0;
    padding: 8px 12px;
    line-height: 24px;
    width: 100%;
    white-space: nowrap;
    cursor: pointer;

    &:hover {
      background-color: $gray_bgr;
    }

    .sprite-icon {
      width: 16px;
      min-width: 16px;
      height: 16px;
      margin-left: 5px;
      color: $gray_l;
      transition: .2s opacity ease;
    }
  }
}
</style>
